import { z } from 'zod';
import {
  FieldPerfRangeSchema,
  type FieldPerfRange,
  type FieldPerfWorst,
  type PerfLabel,
  type PerfMetric,
} from './fieldPerf.schema.js';

// ============================================================
// THE FULL DEVICE REPORT (GET /api/field-perf/report?range=) — what the
// Performance section's "Copy report" adds to the three reads it already holds.
//
// The devices read keeps a row to what the TABLE needs: four headline figures,
// the five slowest screens and the five worst events. An audit of a tablet
// needs the rest — every metric the tablet measured (warm start, the capture
// screen's first frame, the home reads…), the hour of the company day each one
// was slowest in, and every worst event the tablet sent in the range. Fetched
// only when somebody asks for the report, so the table's read stays small.
//
// Same gate (`system.fieldPerf.view`), same rolling range (fieldPerf.range.ts).
// ============================================================

export const FieldPerfReportQuerySchema = z.object({
  range: FieldPerfRangeSchema.default('24h'),
});
export type FieldPerfReportQuery = z.infer<typeof FieldPerfReportQuerySchema>;

/** Most worst events one device sends in the report; `eventsTotal` carries the rest. */
export const PERF_REPORT_EVENTS_CAP = 100;

/** One metric (and label) over the range. p50/p95 are bucket upper edges ("at
 *  most"); `max` is exact. `worstHour` is the company-zone hour of day ("14:00")
 *  whose hourly windows had the highest p95 — null when only compacted DAY
 *  windows measured it, because a day window cannot say which hour. */
export interface FieldPerfTiming {
  m: PerfMetric;
  l: PerfLabel;
  n: number;
  p50: number;
  p95: number;
  max: number;
  worstHour: string | null;
  worstHourP95: number | null;
}

export interface FieldPerfReportRow {
  deviceId: string;
  /** Every metric measured in the range, slowest p95 first. */
  timings: FieldPerfTiming[];
  /** The device's worst single events in the range, newest first, at most
   *  PERF_REPORT_EVENTS_CAP. These are what the tablet chose to send (its
   *  slowest per batch), not every event — the counts are in `timings`. */
  events: FieldPerfWorst[];
  /** How many worst events the range holds, before the cap. */
  eventsTotal: number;
}

export interface FieldPerfReportResponse {
  range: FieldPerfRange;
  generatedAt: string;
  /** The company zone the worst hours were cut in — the report states it. */
  timeZone: string;
  eventsCap: number;
  rows: FieldPerfReportRow[];
}
